import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPause, faPlay } from '@fortawesome/free-solid-svg-icons';
import { ActivityType } from 'types/interfaces/activity';
import { ACTIVITY_TYPE_CONFIG, FILTER_DISPLAY_ORDER } from '../constants';
import './ActivityFilterBar.css';

/** Portal-based tooltip that escapes overflow:hidden containers */
const PillTooltip: React.FC<{ text: string; anchor: DOMRect }> = ({ text, anchor }) => {
  const left = anchor.left + anchor.width / 2;
  const top = anchor.bottom + 8;

  return createPortal(
    <div
      className="activity-filter-bar__tooltip"
      style={{ left, top }}
    >
      {text}
    </div>,
    document.body
  );
};

interface ActivityFilterBarProps {
  activeTypes: Set<ActivityType>;
  toggleType: (type: ActivityType) => void;
  isPaused: boolean;
  togglePause: () => void;
  pendingCount: number;
}

const ActivityFilterBar: React.FC<ActivityFilterBarProps> = ({
  activeTypes,
  toggleType,
  isPaused,
  togglePause,
  pendingCount,
}) => {
  const [tooltip, setTooltip] = useState<{ text: string; rect: DOMRect } | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showTooltip = useCallback((text: string, el: HTMLElement) => {
    timerRef.current = setTimeout(() => {
      setTooltip({ text, rect: el.getBoundingClientRect() });
    }, 300);
  }, []);

  const hideTooltip = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    setTooltip(null);
  }, []);

  // Clean up timer on unmount
  useEffect(() => {
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, []);

  return (
    <div className="activity-filter-bar">
      <div className="activity-filter-bar__pills">
        {FILTER_DISPLAY_ORDER.map((type) => {
          const config = ACTIVITY_TYPE_CONFIG[type];
          const isActive = activeTypes.has(type);

          return (
            <button
              key={type}
              className={`activity-filter-bar__pill${isActive ? ' activity-filter-bar__pill--active' : ''}`}
              onClick={() => toggleType(type)}
              onMouseEnter={(e) => showTooltip(config.tooltip, e.currentTarget)}
              onMouseLeave={hideTooltip}
              style={{
                borderColor: isActive ? config.color : 'rgba(255, 255, 255, 0.15)',
                backgroundColor: isActive ? config.bgColor : 'transparent',
                color: isActive ? config.color : 'rgba(255, 255, 255, 0.35)',
              }}
              type="button"
              aria-pressed={isActive}
              aria-label={`Filter ${config.label} activities: ${config.tooltip}`}
            >
              <FontAwesomeIcon icon={config.icon} className="activity-filter-bar__pill-icon" />
              {config.label}
            </button>
          );
        })}
      </div>

      {tooltip && <PillTooltip text={tooltip.text} anchor={tooltip.rect} />}

      <button
        className={`activity-filter-bar__pause${isPaused ? ' activity-filter-bar__pause--active' : ''}`}
        onClick={togglePause}
        type="button"
        aria-label={isPaused ? `Resume feed (${pendingCount} pending)` : 'Pause feed'}
      >
        <FontAwesomeIcon icon={isPaused ? faPlay : faPause} />
        <span>{isPaused ? 'Resume' : 'Pause'}</span>
        {isPaused && pendingCount > 0 && (
          <span className="activity-filter-bar__pending-badge">{pendingCount}</span>
        )}
      </button>
    </div>
  );
};

export default ActivityFilterBar;
